import React, { useState } from "react";
import { Box, Grid, Stack, ToggleButton, ToggleButtonGroup, useTheme } from "@mui/material";
import FavoriteIcon from "@mui/icons-material/Favorite";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import AirIcon from "@mui/icons-material/Air";
import WaterDropIcon from "@mui/icons-material/WaterDrop";
import { StatCard, LineChart } from "../components/charts";
import { DashboardCard } from "../components/DashboardCard";
import { biometricsStats, biometricsTrends, respiratoryPatterns, skinTempPatterns } from "../data";

export const Biometrics: React.FC = () => {
  const theme = useTheme();
  const [trendView, setTrendView] = useState("avg_heart_rate");

  const statCards = [
    { metric: biometricsStats.resting_heart_rate, icon: FavoriteIcon },
    { metric: biometricsStats.hrv, icon: MonitorHeartIcon },
    { metric: biometricsStats.respiratory_rate, icon: AirIcon },
    { metric: biometricsStats.spo2, icon: WaterDropIcon },
  ];

  const getTrendLabel = () => {
    switch (trendView) {
      case "avg_heart_rate": return "Avg Heart Rate";
      case "max_heart_rate": return "Max Heart Rate";
      case "hrv": return "HRV";
      case "resting_heart_rate": return "Resting Heart Rate";
      case "respiratory_rate": return "Respiratory Rate";
      case "spo2": return "SpO2";
      default: return "Avg Heart Rate";
    }
  };

  const getTrendColor = () => {
    switch (trendView) {
      case "avg_heart_rate": return theme.palette.info.main;
      case "max_heart_rate": return theme.palette.error.main;
      case "hrv": return theme.palette.success.main;
      case "resting_heart_rate": return theme.palette.warning.main;
      case "respiratory_rate": return theme.palette.warning.main;
      case "spo2": return theme.palette.success.main;
      default: return theme.palette.info.main;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Grid container spacing={3}>
          {statCards.map((stat, index) => (
            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
              <StatCard metric={stat.metric} icon={stat.icon} />
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <DashboardCard
              title="Biometrics Trends"
              subtitle="Daily vital sign metrics over time."
              titleComponent={
                <ToggleButtonGroup
                  value={trendView}
                  exclusive
                  onChange={(_, v) => { if (v !== null) setTrendView(v); }}
                  size="small"
                >
                  <ToggleButton value="avg_heart_rate">Avg HR</ToggleButton>
                  <ToggleButton value="max_heart_rate">Max HR</ToggleButton>
                  <ToggleButton value="hrv">HRV</ToggleButton>
                  <ToggleButton value="resting_heart_rate">Resting HR</ToggleButton>
                  <ToggleButton value="respiratory_rate">Resp. Rate</ToggleButton>
                  <ToggleButton value="spo2">SpO2</ToggleButton>
                </ToggleButtonGroup>
              }
            >
              <LineChart
                data={biometricsTrends}
                xAxisKey="label"
                series={[{ dataKey: trendView, label: getTrendLabel(), color: getTrendColor(), area: true }]}
                height={300}
                yAxisMin={0}
              />
            </DashboardCard>
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <DashboardCard
              title="Respiratory Rate Patterns"
              subtitle="24-hour respiratory rate variation."
            >
              <LineChart
                data={respiratoryPatterns}
                xAxisKey="time"
                series={[{ dataKey: "value", label: "Respiratory Rate (brpm)", color: theme.palette.warning.main, area: true }]}
                height={300}
                yAxisMin={10}
                yAxisMax={22}
              />
            </DashboardCard>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <DashboardCard
              title="Skin Temperature Patterns"
              subtitle="24-hour skin temperature variation."
            >
              <LineChart
                data={skinTempPatterns}
                xAxisKey="time"
                series={[{ dataKey: "value", label: "Skin Temp", color: theme.palette.error.main, area: true }]}
                height={300}
              />
            </DashboardCard>
          </Grid>
        </Grid>
      </Stack>
    </Box>
  );
};
